import { useLocalStorageState } from "ahooks";
import dayjs from "dayjs";
import { SWRConfig } from "swr";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useSearchParams } from "react-router-dom";

import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { fakeData } from "@/_mocks/fakeData";
import { getFlightMegaListV2, getRouteCheck } from "@/api";
import FilterBar from "@/components/FilterBar";
import FlightList from "@/components/FlightList";
import FlightListV2 from "@/components/FlightListV2";
import FlightListSkeleton from "@/components/FlightListV2/Skeleton";
import IntroMega from "@/components/IntroMega";
import FilterRoundTrip from "@/components/RoundTripTab";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import SearchBar from "@/components/SearchBar/MegaIndex";
import Sort from "@/components/Sort";
import { sortFn } from "@/components/Sort/sortConfig";
import { FlightWayEnum, RoundTripTypeEnum } from "@/constants";
import { API_KEY } from "@/constants/config";
import { megaProgramsOptions } from "@/constants/mega";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useCheckSearchCondition from "@/hooks/useCheckMegaSearchCondition";
import useEvent from "@/hooks/useEvent";
import { SearchConditionType } from "@/hooks/useMegaSearchCondition";
import useMegaSearchLimit from "@/hooks/useMegaSearchLimit";
import useNavigateLogin from "@/hooks/useNavigateLogin";
import usePlatform from "@/hooks/usePlatform";
import useSearchQuery from "@/hooks/useSearchQuery";
import useThemeData from "@/hooks/useThemeData";
import useUserInfo from "@/hooks/useUserInfo";
import { FlightItem as FlightItemType } from "@/typings";
import { createSearchQuery, parseSearchQuery } from "@/utils/searchQuery";

const FlightSearch = () => {
  const { isMobile, isPc } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkSearchCondition } = useCheckSearchCondition();
  const theme = useThemeData();

  const { mutate } = useMegaSearchLimit();

  const conditionParams = useRef(parseSearchQuery(searchParams));

  const [isLoading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(true);

  const [listType, setListType] = useLocalStorageState("mega-list-view-type", {
    defaultValue: 2,
  });

  const [searchData, setSearchData] = useState<FlightItemType[]>(fakeData.data || []);
  const [isFakeData, setIsFakeData] = useState(true);
  const [filterData, setFilterData] = useState<FlightItemType[]>([]);
  const [sortKey, setSortKey] = useState(1);
  const { getUserInfo } = useUserInfo();

  const [roundTripType, setRoundTripType] = useState<RoundTripTypeEnum>(RoundTripTypeEnum.Outbound);

  const { query } = useSearchQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOpenV2, setDialogOpenV2] = useState(false);
  const [routePath, setRoutePath] = useState(undefined);

  const store = useGlobalStoreContext();
  const showUpsell = store.show_upsell;

  const { navigateLogin } = useNavigateLogin();

  const appendSearchData = (data: FlightItemType[]) => {
    setSearchData(data);
  };

  const openDialog = () => {
    setDialogOpen(true);
  };

  const openDialogV2 = () => {
    setDialogOpenV2(true);
  };

  const handleSearch = useEvent(async (condition: SearchConditionType) => {
    const userInfo = await getUserInfo();
    if (!userInfo.token) {
      setLoggedIn(false);
      navigateLogin("/signup");
      return;
    }
    const supportedPrograms = megaProgramsOptions.map((item) => item.name);
    const supportedProgramsSet = new Set(supportedPrograms);
    // console.log("supportedProgramsSet:", supportedProgramsSet);
    // Get current path params
    setIsFakeData(false);

    if (condition) {
      const errMsg = checkSearchCondition(condition);

      if (errMsg) {
        setLoading(false);
        return;
      }

      // update url path query
      const nextParams = createSearchQuery({ ...condition });
      const nextSearch = createSearchParams(nextParams).toString();

      setSearchParams(nextSearch);

      const date_range = { from: "", to: "" };
      let return_date_range = null;

      if (condition.flightWay === FlightWayEnum.OneWay) {
        if (condition.range) {
          date_range.from = dayjs(condition.oneWayRangeStartDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.oneWayRangeEndDate).format("YYYY-MM-DD");
        } else {
          date_range.from = dayjs(condition.oneWayDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.oneWayDate).format("YYYY-MM-DD");
        }
      } else {
        if (condition.range) {
          date_range.from = dayjs(condition.departureRangeStartDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.departureRangeEndDate).format("YYYY-MM-DD");

          return_date_range = {
            from: dayjs(condition.returnRangeStartDate).format("YYYY-MM-DD"),
            to: dayjs(condition.returnRangeEndDate).format("YYYY-MM-DD"),
          };
        } else {
          date_range.from = dayjs(condition.roundTripDepartureDate).format("YYYY-MM-DD");
          date_range.to = dayjs(condition.roundTripDepartureDate).format("YYYY-MM-DD");

          return_date_range = {
            from: dayjs(condition.roundTripReturnDate).format("YYYY-MM-DD"),
            to: dayjs(condition.roundTripReturnDate).format("YYYY-MM-DD"),
          };
        }
      }

      // Filtered programs by supported programs
      const filteredPrograms = condition.programs.filter((program) =>
        supportedProgramsSet.has(program)
      );

      // Check if the current url is prod
      const params = {
        search_type: condition.flightWay,
        pax: condition.pax,
        children: condition.children,
        cabins: condition.cabins.split("&").map((item) => item.trim()),
        departure_airports: condition.fromLocation.map((ele) => ele.iata),
        arrival_airports: condition.toLocation.map((ele) => ele.iata),
        date_range,
        api_key: API_KEY,
        type: "",
        task_id: "",
        task_ct: 1,
        programs: filteredPrograms,
      };

      if (return_date_range) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        params.return_date_range = return_date_range;
      }

      try {
        getRouteCheck(condition, setRoutePath);
        await setLoading(true);
        await setSearchData([]);

        await getFlightMegaListV2(
          params,
          appendSearchData,
          userInfo.token,
          condition,
          showUpsell,
          openDialog,
          openDialogV2,
          setLoading
        );
        mutate();

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  });

  const filterChange = useCallback(
    (v: FlightItemType[]) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);
    },
    [sortKey]
  );

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);
    },
    [filterData]
  );

  useEffect(() => {
    if (window.location.search && conditionParams.current) {
      handleSearch(conditionParams.current);
    } else {
      setLoading(false);
    }
  }, [handleSearch]);

  // 计算后的原数据，处理roundtrip类型过滤
  const computedSearchData = useMemo(() => {
    if (query.flightWay !== FlightWayEnum.RoundTrip) {
      return searchData;
    }

    return searchData.filter((item) => {
      return roundTripType === RoundTripTypeEnum.Return ? item.return_trip : !item.return_trip;
    });
  }, [searchData, roundTripType, query.flightWay]);

  const [totalTitle, setTotalTitle] = useState("");

  useEffect(() => {
    let text = "";
    if (isFakeData) {
      text = "Example " + `${fakeData.data.length}` + " flights";
      // text = "Example " + " flights";
    } else if (computedSearchData.length > 0) {
      if (filterData.length === computedSearchData.length) {
        text = `${computedSearchData.length}` + " flights";
      } else {
        text = `${filterData.length}/${computedSearchData.length}` + " flights";
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [computedSearchData, filterData]);

  const showPrompt = (simpler = false) => {
    return (
      <div
        className={
          isPc
            ? "mx-[10px] mt-[15px] items-center justify-center text-center"
            : "mx-[10px] mt-[15px] items-center"
        }
      >
        <Typography variant="h6" color="textPrimary">
          Welcome!{" "}
          <Typography
            component={Link}
            onClick={() => navigateLogin("/signup")}
            color="primary"
            variant="h6"
            className="inline"
          >
            Save thousands and hours
          </Typography>{" "}
          with a{" "}
          <Typography
            component={Link}
            onClick={() => navigateLogin("/signup")}
            color="primary"
            variant="h6"
            className="inline"
          >
            free
          </Typography>{" "}
          account, necessary to fully enjoy real-time search across all programs.{" "}
          {!simpler && (
            <Typography variant="h6" color="textPrimary">
              <Typography
                component={Link}
                color="primary"
                variant="h6"
                className="inline"
                onClick={() => navigateLogin("/signup")}
              >
                Sign up
              </Typography>{" "}
              now to enjoy your search! Or try our{" "}
              <Typography
                component={Link}
                href="/routes"
                color="primary"
                variant="h6"
                className="inline"
              >
                Panorama
              </Typography>{" "}
              feature for{" "}
              <Typography color="primary" variant="h6" className="inline">
                free
              </Typography>
              .
            </Typography>
          )}
        </Typography>

        <div
          className={
            isPc
              ? "flex items-center justify-center text-center w-[100%]"
              : "flex items-center w-[100%]"
          }
        >
          <Typography
            className="flex items-center justify-center"
            variant="body1"
            color="textPrimary"
          >
            Thanks for choosing us for your award travel hunting! 😃 🚀 ✨
            {/* <TagFacesIcon sx={{ margin: "auto", marginLeft: "10px" }} /> */}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <SWRConfig>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Mega Search </title>
        <meta
          name="description"
          content="Select your favorite programs, search 24-entries, and get data in 20 seconds."
        />
      </Helmet>
      {isMobile ? (
        <div className="px-[15px]">
          <SearchBar withMobileSearchBtn onSearch={handleSearch} isLoading={isLoading} />
          <div className="mt-[20px]">
            <FilterBar
              dataSourceList={computedSearchData}
              onChange={filterChange}
              roundTripType={roundTripType}
            />
            {query.flightWay === FlightWayEnum.RoundTrip ? (
              <FilterRoundTrip value={roundTripType} onChange={setRoundTripType} />
            ) : null}
            <div className="mt-[20px]">
              {" "}
              <IntroMega />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <Box className="pb-[12px]" sx={{ boxShadow: 4 }}>
            <div className="px-[24px]">
              <SearchBar onSearch={handleSearch} withSearchBtn isLoading={isLoading} />
            </div>
          </Box>

          <div className="px-[24px] my-[20px]">
            <FilterBar
              dataSourceList={computedSearchData}
              onChange={filterChange}
              roundTripType={roundTripType}
              useWhere="mega"
            />
            {query.flightWay === FlightWayEnum.RoundTrip ? (
              <FilterRoundTrip value={roundTripType} onChange={setRoundTripType} />
            ) : null}
          </div>

          <div className="px-[24px]">
            <IntroMega />
          </div>
        </div>
      )}

      <div className={isMobile ? "px-[15px]" : "px-[24px]"}>
        {isLoading ? (
          <div className="mt-[25px]">
            <LinearProgress />
          </div>
        ) : null}
        {!loggedIn && showPrompt()}
        {searchData.length > 0 && (
          <div className="flex items-center justify-between pt-[10px] pb-[10px]">
            <div className="flex items-center">
              <Typography variant="body1" color="textPrimary" sx={{ marginRight: "10px" }}>
                {totalTitle}
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={listType}
                exclusive
                onChange={(e, v) => {
                  setListType(v);
                }}
                aria-label="text alignment"
                sx={{
                  ".MuiToggleButton-root": {
                    padding: "3px",
                  },
                }}
              >
                <ToggleButton value={2} aria-label="list">
                  <ViewHeadlineIcon />
                </ToggleButton>
                <ToggleButton value={1} aria-label="table">
                  <TableRowsRoundedIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <Sort value={sortKey} onChange={sortChange} />
          </div>
        )}
        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            <Typography color="textSecondary">No results match your current filters.</Typography>
            <Typography color="textSecondary">
              Try adjusting your filters, updating your search criteria, or refreshing the page.
            </Typography>
            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div>
            {filterData.length ? (
              <>
                {listType === 1 ? (
                  <FlightList data={!loggedIn ? filterData.slice(0, 3) : filterData} />
                ) : (
                  <FlightListV2 data={!loggedIn ? filterData.slice(0, 3) : filterData} />
                )}
              </>
            ) : null}

            {filterData.length > 1 && !loggedIn && showPrompt(true)}
            {isLoading || !loggedIn ? (
              <FlightListSkeleton
                count={!loggedIn ? Math.min(1, filterData.length) : filterData.length}
              />
            ) : null}
          </div>
        )}
      </div>

      <Dialog
        onClose={() => {
          setDialogOpen(false);
        }}
        open={dialogOpen}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Search Condition Error</Typography>
        </DialogTitle>
        <DialogContent dividers className="pt-[20px] !px-[20px]">
          <Typography variant="body1" color="error" gutterBottom>
            Your search range is too broad.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please reduce the number of days in your search range.
          </Typography>
          <Typography variant="body2" color="textSecondary" className="mt-[10px]">
            Try searching for shorter date ranges or narrowing your location criteria.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        onClose={() => {
          setDialogOpenV2(false);
        }}
        open={dialogOpenV2}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Searching Too Frequently</Typography>
        </DialogTitle>
        <DialogContent className="pt-[20px] !px-[20px]">
          It seems you've performed an excessive number of searches within a brief timeframe. Please
          take a short rest {":)"}. Thanks for choosing us for your award travel hunting!
        </DialogContent>
      </Dialog>
      <ScrollTopBar />
    </SWRConfig>
  );
};

export default FlightSearch;
